import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, ChevronRight, Mail, UserPlus, X } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useClinicUsersList,
  usePendingInvitations,
  type ClinicMemberWithUser,
  type ClinicRole,
  type PendingInvitation,
} from '@/hooks/useClinicUsers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { callFunction, ApiCallError } from '@/lib/api';
import { resolveMemberPhotoUrl } from '@/lib/memberPhoto';
import { SectionCard } from './ClinicGeneral';

export function ClinicUsers() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();

  const canEdit = Boolean(activeClinicId && hasPermission(activeClinicId, 'settings_users', 'update'));

  const [includeInactive, setIncludeInactive] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const {
    data: members,
    isLoading,
    error,
  } = useClinicUsersList(activeClinicId, {
    includeInactive,
  });
  const {
    data: pendingInvites,
    isLoading: pendingLoading,
    error: pendingError,
  } = usePendingInvitations(activeClinicId);

  if (isLoading || pendingLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error || pendingError) return <ErrorAlert message={t('users.load_error')} />;

  const hasMembers = Boolean(members && members.length > 0);
  const hasPending = Boolean(pendingInvites && pendingInvites.length > 0);

  return (
    <div className="space-y-6">
      <SectionCard title={t('users.title')} subtitle={t('users.subtitle')} readOnly={!canEdit}>
        <div className="mb-4 flex items-center justify-between">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t('users.show_inactive')}
          </label>
          {canEdit && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <UserPlus className="h-4 w-4" />
              {t('users.invite_user')}
            </button>
          )}
        </div>

        {!hasMembers && !hasPending ? (
          <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {t('users.empty')}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <Th>{t('users.col_name')}</Th>
                  <Th>{t('users.col_email')}</Th>
                  <Th>{t('users.col_role')}</Th>
                  <Th>{t('users.col_title')}</Th>
                  <Th>{t('users.col_status')}</Th>
                  <th scope="col" className="relative px-3 py-3.5">
                    <span className="sr-only">{t('users.col_actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                {pendingInvites?.map((invite) => (
                  <PendingInviteRow key={invite.id} invite={invite} />
                ))}
                {members?.map((m) => (
                  <UserRow key={m.id} member={m} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {inviteOpen && activeClinicId && (
        <InviteUserModal clinicId={activeClinicId} onClose={() => setInviteOpen(false)} />
      )}
    </div>
  );
}

function PendingInviteRow({ invite }: { invite: PendingInvitation }) {
  const { t } = useTranslation();
  const name = invite.display_name?.trim() || invite.email;
  const expired = new Date(invite.expires_at).getTime() < Date.now();

  return (
    <tr className="bg-amber-50/40 dark:bg-amber-950/10">
      <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
        <div className="flex items-center gap-3">
          <Avatar name={name} color="#D97706" photoUrl={null} />
          <div>
            <div>{name}</div>
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {t('users.pending_invite_hint')}
            </div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
        <span className="inline-flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-gray-400" />
          {invite.email}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm">
        <RoleBadge role={invite.role} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">—</td>
      <td className="whitespace-nowrap px-3 py-3 text-sm">
        {expired ? (
          <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
            {t('users.status_invite_expired')}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
            {t('users.status_pending')}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-400">
        {t('users.pending_awaiting_accept')}
      </td>
    </tr>
  );
}

function UserRow({ member }: { member: ClinicMemberWithUser }) {
  const { t } = useTranslation();
  const name = member.user?.name?.trim() || member.user?.email || '—';

  return (
    <tr>
      <td className="whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900 dark:text-white">
        <div className="flex items-center gap-3">
          <Avatar name={name} color={member.color} photoUrl={member.photo_url} />
          <div>
            <div>{name}</div>
            {member.user?.phone && (
              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {member.user.phone}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
        <span className="inline-flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-gray-400" />
          {member.user?.email ?? '—'}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm">
        <RoleBadge role={member.role} />
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
        {member.title ?? '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-sm">
        {member.status === 'active' ? (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            {t('users.status_active')}
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
            {t('users.status_inactive')}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-right text-sm">
        <Link
          to={`/settings/users/${member.id}`}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          {t('users.see_details')}
          <ChevronRight className="ml-1 h-4 w-4" />
        </Link>
      </td>
    </tr>
  );
}

export function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation();
  const tone =
    role === 'main_clinic_admin'
      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
      : role === 'clinic_admin'
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  const label = t(`users.role_${role}`, role);
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {label}
    </span>
  );
}

export function Avatar({
  name,
  color,
  photoUrl,
  size = 'md',
}: {
  name: string;
  color: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim =
    size === 'lg' ? 'h-12 w-12 text-base' : size === 'sm' ? 'h-8 w-8 text-xs' : 'h-9 w-9 text-sm';
  const resolvedPhoto = resolveMemberPhotoUrl(photoUrl);
  if (resolvedPhoto) {
    return (
      <img
        src={resolvedPhoto}
        alt={name}
        className={`${dim} rounded-full object-cover ring-1 ring-inset ring-black/10`}
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      className={`${dim} flex items-center justify-center rounded-full font-semibold uppercase text-white ring-1 ring-inset ring-black/10`}
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {initials || '?'}
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
    >
      {children}
    </th>
  );
}

// ---------------------------------------------------------------------------
// InviteUserModal — calls the `invite-user` Edge Function
// ---------------------------------------------------------------------------

interface InviteUserOutput {
  invitation_id: string;
  expires_at: string;
  email: string;
}

function useInviteUser(clinicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      email: string;
      role: Extract<ClinicRole, 'clinic_admin' | 'employee'>;
      custom_role_id?: string | null;
      display_name?: string;
    }) =>
      callFunction<
        {
          clinic_id: string;
          email: string;
          role: Extract<ClinicRole, 'clinic_admin' | 'employee'>;
          custom_role_id?: string | null;
          display_name?: string;
        },
        InviteUserOutput
      >('invite-user', {
        clinic_id: clinicId,
        email: vars.email,
        role: vars.role,
        custom_role_id: vars.custom_role_id ?? null,
        display_name: vars.display_name || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId] });
      qc.invalidateQueries({ queryKey: ['clinic-users', 'pending-invitations', clinicId] });
    },
  });
}

/** Invite dropdown value: clinic admin, or a clinic_roles id from the matrix. */
type InviteRoleValue = 'clinic_admin' | `role:${string}`;

function InviteUserModal({ clinicId, onClose }: { clinicId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: clinicRoles, isLoading: rolesLoading } = useClinicRoles(clinicId);
  const [email, setEmail] = useState('');
  const [roleValue, setRoleValue] = useState<InviteRoleValue | ''>('');
  const [displayName, setDisplayName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);

  const { mutate: inviteUser, isPending } = useInviteUser(clinicId);

  const matrixRoles = clinicRoles ?? [];

  useEffect(() => {
    if (roleValue !== '' || matrixRoles.length === 0) return;
    const employeeTemplate = matrixRoles.find((r) => r.system_key === 'employee');
    const defaultId = employeeTemplate?.id ?? matrixRoles[0].id;
    setRoleValue(`role:${defaultId}`);
  }, [matrixRoles, roleValue]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!roleValue) {
      setFormError(t('users.invite_error_failed'));
      return;
    }

    const payload =
      roleValue === 'clinic_admin'
        ? {
            email,
            role: 'clinic_admin' as const,
            custom_role_id: null,
            display_name: displayName || undefined,
          }
        : {
            email,
            role: 'employee' as const,
            custom_role_id: roleValue.slice('role:'.length),
            display_name: displayName || undefined,
          };

    inviteUser(payload, {
      onSuccess: (data) => {
        setSuccessEmail(data.email);
      },
      onError: (err) => {
        if (err instanceof ApiCallError && err.code === 'conflict') {
          setFormError(t('users.invite_error_conflict'));
        } else {
          setFormError(t('users.invite_error_failed'));
        }
      },
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-modal-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2
            id="invite-modal-title"
            className="text-base font-semibold text-gray-900 dark:text-white"
          >
            {t('users.invite_modal_title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {successEmail ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('users.invite_success', { email: successEmail })}
              </p>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label
                  htmlFor="invite-email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t('users.invite_field_email')}
                </label>
                <input
                  id="invite-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>

              {/* Role — clinic admin + roles from the permissions matrix */}
              <div>
                <label
                  htmlFor="invite-role"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t('users.invite_field_role')}
                </label>
                <select
                  id="invite-role"
                  value={roleValue}
                  onChange={(e) => setRoleValue(e.target.value as InviteRoleValue)}
                  disabled={rolesLoading || matrixRoles.length === 0}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="clinic_admin">{t('users.role_clinic_admin')}</option>
                  {matrixRoles.map((r) => (
                    <option key={r.id} value={`role:${r.id}`}>
                      {r.name}
                      {r.is_system ? ` (${t('roles.system_badge')})` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">{t('users.invite_role_hint')}</p>
              </div>

              {/* Display name (optional) */}
              <div>
                <label
                  htmlFor="invite-name"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t('users.invite_field_name')}
                  <span className="ml-1 text-xs text-gray-400">{t('common.optional')}</span>
                </label>
                <input
                  id="invite-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={120}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-400">{t('users.invite_field_name_hint')}</p>
              </div>

              {formError && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  {formError}
                </p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isPending}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60"
                >
                  {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isPending ? t('users.invite_sending') : t('users.invite_submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
